import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
  useImperativeHandle,
} from 'react'
import cx from 'classnames'
import FocusLock from 'react-focus-lock'
import { v4 as uuid } from 'uuid'

import { AdoptionComponent } from '../../../constants/AdoptionComponent'
import useRoveFocus from '../../../hooks/useRoveFocus'
import { useDsAdoptionAttribute } from '../../../utils/dsAdoption'
import { fixedForwardRef } from '../../../utils/fixedForwardRef'
import { getString } from '../../../utils/localization/format'
import Popover from '../../layers/Popover'
import { FormFieldLabelContainer, FormFieldTextBelow } from '../FormField'
import { OptionsList, BasicOption } from '../OptionsList'

import { SelectProps } from './interfaces'
import { SelectAnchor } from './SelectAnchor'
import { SelectSearch } from './SelectSearch'

import cm from './Select.module.css'

const defaultOptionFilter = (label: string, query: string) =>
  !query || getString(label).toLowerCase().indexOf(query.toLowerCase()) >= 0

const SelectInner = <OptionType extends BasicOption>(
  {
    variant = 'default',
    options: initialOptions,
    value = null,
    placeholder,
    search = false,
    searchLabel,
    emptyLabel,
    className,
    popoverWidth,
    disableAutoScroll,
    renderOption: renderOptionProp,
    loading = false,
    renderCustomFirstComponent,
    renderCustomLastComponent,
    renderAnchor,
    filterFn: filterFnProp,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    onKeyUp,
    /** inherited from FormFieldLabelContainer */
    label,
    infoText,
    infoTextWidth,
    lozenge,
    id,
    labelId,
    disabled = false,
    invalid = false,
    helpText,
    errorText,
    ariaLabel,
    ariaLabelledBy,
    onSearch,
    labelClassName,
  }: SelectProps<OptionType>,
  forwardedRef: React.Ref<HTMLButtonElement | null>,
) => {
  const [query, setQuery] = useState<string>('')
  const [isOpen, setIsOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState(value)

  const selectRef = useRef<HTMLButtonElement | null>(null)

  useImperativeHandle(forwardedRef, () => selectRef.current, [])

  const selectLabelId = React.useMemo(() => labelId || `${uuid()}-input`, [labelId])
  const adoptionAttribute = useDsAdoptionAttribute(AdoptionComponent.SELECT)

  // Ensure the options have ids
  const options = useMemo(() => {
    return initialOptions.map((option) => {
      if (option.id) return option
      return {
        ...option,
        id: `select-option-${String(option.value).replace(/\W/g, '-')}-${uuid()}`,
      }
    })
  }, [initialOptions])

  const filteredOptions = useMemo(() => {
    /** if search is not enabled, options do not need filtering */
    if (!search || query?.length === 0) return options

    const filterFn = filterFnProp || defaultOptionFilter
    const filtered: OptionType[] = []
    let currentGroup: OptionType | null = null
    options.forEach((option) => {
      if (option.type === 'group') {
        currentGroup = option
      } else {
        if (filterFn(option.label, query ?? '')) {
          /**  if option should be included to filtered result */
          if (currentGroup) {
            /** first add group header */
            filtered.push(currentGroup)
            /** reset current group */
            currentGroup = null
          }
          /** add option to the list */
          filtered.push(option)
        }
      }
    })
    return filtered
  }, [filterFnProp, options, query, search])

  const focusableOptionIds = useMemo(() => {
    return filteredOptions.reduce((acc, option) => {
      if (option.type === 'group') return acc
      if (!option.id) return acc
      return [...acc, option.id]
    }, [] as string[])
  }, [filteredOptions])

  const {
    focusedId,
    handleKeyDown: handleArrowsKeyDown,
    setFocusedId,
  } = useRoveFocus(focusableOptionIds)

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLButtonElement>) => {
      if (disabled) return
      if (onFocus) onFocus(event)
    },
    [disabled, onFocus],
  )

  const handleButtonKeydown = useCallback(
    (event: React.KeyboardEvent) => {
      onKeyDown?.(event)
      if (isOpen) return
      if (
        event.key === 'ArrowDown' ||
        event.key === 'ArrowUp' ||
        event.key === 'Enter' ||
        event.code === 'Space'
      ) {
        event.preventDefault()
        setIsOpen(true)
      }
    },
    [isOpen, onKeyDown],
  )

  const handleButtonClick = useCallback(() => {
    setIsOpen((isOpen) => {
      return !isOpen
    })
  }, [])

  const handleRequestClose = useCallback(
    (
      reason: string,
      event: (KeyboardEvent | PointerEvent) & { stopPropagationToLayers: () => void },
    ) => {
      if (reason === 'clickAnchor') {
        return
      }
      if (reason === 'clickAway' || reason === 'esc') {
        setIsOpen(false)
      }
      if (reason !== 'offScreen') {
        event.stopPropagationToLayers()
      }
    },
    [],
  )

  useEffect(() => {
    if (isOpen) {
      setFocusedId(selectedOption?.id ?? focusableOptionIds[0])
    } else {
      setFocusedId(undefined)
      if (search) {
        setQuery('')
      }
    }
  }, [focusableOptionIds, isOpen, search, selectedOption?.id, setFocusedId])

  useEffect(() => {
    if (value && selectedOption?.value !== value?.value) {
      setSelectedOption(value)
    }
  }, [value, selectedOption])

  const handleSelect = useCallback(
    (option: OptionType) => {
      if (option?.disabled) return
      setIsOpen(false)
      setSelectedOption(option)
      if (onChange && Boolean(option)) {
        onChange(option)
      }
    },
    [onChange],
  )

  const submitSelectedOption = useCallback(() => {
    const selectedOption = options.find((option) => option.id === focusedId)

    if (selectedOption && !selectedOption?.disabled) {
      handleSelect(selectedOption)
    }
  }, [focusedId, handleSelect, options])

  const handleKeydown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.code === 'ArrowDown' || event.code === 'ArrowUp') {
        handleArrowsKeyDown(event)
      } else if (event.code === 'Home') {
        return setFocusedId(focusableOptionIds[0])
      } else if (event.code === 'End') {
        return setFocusedId(focusableOptionIds[focusableOptionIds.length - 1])
      } else if (event.code === 'Enter') {
        submitSelectedOption()
      } else if (event.code === 'Space' && !search) {
        submitSelectedOption()
      }
    },
    [focusableOptionIds, handleArrowsKeyDown, setFocusedId, search, submitSelectedOption],
  )

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLButtonElement, Element>) => {
      if (disabled) return
      onBlur?.(event)
    },
    [disabled, onBlur],
  )

  const handleReturnFocus = useCallback(() => {
    selectRef.current?.focus()
    return false
  }, [])

  const popoverProps = useMemo(
    () => ({
      onRequestClose: handleRequestClose,
      useLayerForClickAway: false,
      noContentPadding: true,
      anchor: selectRef.current,
      open: isOpen,
      fitTargetWidth: !popoverWidth,
      at: 'bottom-left',
      autoPosition: 'shift flip',
      fullScreen: false,
      style: { width: popoverWidth },
    }),
    [handleRequestClose, isOpen, popoverWidth],
  )

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (onSearch) {
        onSearch(event)
      }

      setQuery(event.target.value)
    },
    [setQuery, onSearch],
  )

  return (
    <>
      {label && (
        <FormFieldLabelContainer
          id={selectLabelId}
          label={label}
          infoText={infoText}
          lozenge={lozenge}
          infoTextWidth={infoTextWidth}
          disabled={disabled}
          fieldClasses={{ label: 'text-sm' }}
          labelClassName={labelClassName}
        />
      )}
      <div
        className={cx(cm.selectContainer, className, {
          [cm.inline]: variant === 'inline',
          'm-t-xs': Boolean(label),
        })}
        {...adoptionAttribute}
      >
        <button
          id={id}
          type="button"
          className={cx(cm.select, {
            [cm.disabled]: disabled,
            [cm.invalid]: invalid,
            [cm.inline]: variant === 'inline',
            [cm.active]: isOpen,
          })}
          onFocus={handleFocus}
          ref={selectRef}
          disabled={disabled}
          aria-labelledby={ariaLabelledBy || (label && selectLabelId)}
          aria-label={ariaLabel || label}
          aria-haspopup="listbox"
          role="combobox"
          aria-expanded={isOpen}
          // onMouseDown should be used instead onClick, because browsers trigger click event when button receives focus
          onMouseDown={handleButtonClick}
          onBlur={handleBlur}
          onKeyDown={handleButtonKeydown}
          aria-activedescendant={focusedId}
          data-test-id={id}
        >
          <SelectAnchor
            renderAnchor={renderAnchor}
            selectedOption={selectedOption}
            placeholder={placeholder}
            value={value}
          />
        </button>
        <FormFieldTextBelow
          helpText={helpText}
          fieldClasses={{ helpText: 'm-t-xs' }}
          errorText={errorText}
          disabled={disabled}
          showError={invalid}
        />
      </div>
      <Popover className={cm.popover} {...popoverProps}>
        <FocusLock returnFocus={handleReturnFocus}>
          {search && (
            <SelectSearch
              searchLabel={searchLabel}
              query={query}
              onChange={handleSearchChange}
              activeItemId={focusedId}
              onKeydown={handleKeydown}
            />
          )}
          {renderCustomFirstComponent && renderCustomFirstComponent()}
          <OptionsList
            options={filteredOptions}
            loading={loading}
            renderOption={renderOptionProp}
            focusedId={focusedId}
            onKeyUp={onKeyUp}
            emptyLabel={emptyLabel}
            tabIndex={search ? -1 : 0}
            selectedOption={selectedOption}
            handleSelect={handleSelect}
            setFocusedId={setFocusedId}
            disableAutoScroll={disableAutoScroll}
            onKeydown={handleKeydown}
          />
          {renderCustomLastComponent && renderCustomLastComponent()}
        </FocusLock>
      </Popover>
    </>
  )
}

/**
 * The Select component provides a dropdown menu for choosing one or multiple options from a predefined list.
 *
 * [Figma](https://www.figma.com/design/Xf38PdCKeISWnBoh0pLFIIWf/%F0%9F%A7%B1-MC-*Desktop-Components?node-id=28336-5455&node-type=frame&m=dev)
 *
 */
export const Select = fixedForwardRef(SelectInner)
